export type Point = [number, number];

export interface SubmissionGPSPoint {
    x: number;
    y: number;
    lat: number;
    lon: number;
}

export interface SubmissionParkingSpace {
    id: string;
    label: string;
    anchor: Point;
    angle: number;
    lengthFt: number;
    widthFt: number;
}

export interface SubmissionGeoCalibration {
    originLat: number;
    originLon: number;
    pixelsToLocal: number[][];
}

export interface SubmissionYardSection {
    id: string;
    label: string;
    rect: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    entry: Point;
    orientationDeg: number;
}

export interface SubmissionBuilding {
    id: string;
    label: string;
    points: Point[];
}

export interface SubmissionPod {
    px: Point;
}

export interface SubmissionScanner {
    id: string;
    px: Point;
    orientationDeg: number;
}

export interface SubmissionOverflowBlock {
    id: string;
    label: string;
    points: Point[];
    orientationDeg: number;
    active: boolean;
}

export interface SubmissionNoParkingZone {
    id: string;
    points: Point[];
}

export interface SubmissionCrosswalk {
    id: string;
    p1: Point;
    p2: Point;
    widthFt: number;
}

export interface SetupSubmissionInput {
    yardInfo: {
        name: string;
        address: string;
        zipCode: string;
        altitudeA: string;
        altitudeB: string;
        altitudeC: string;
    };

    imageWidth: number;
    imageHeight: number;

    gpsPoints: (SubmissionGPSPoint | null)[];
    geofencePoints: Point[];

    scalePxLength: number | null;
    scaleFeet: string;
    metersPerPx: number | null;

    parkingSpaces: SubmissionParkingSpace[];
    sections: SubmissionYardSection[];
    buildings: SubmissionBuilding[];
    pod: SubmissionPod | null;
    scanners: SubmissionScanner[];
    overflowBlocks: SubmissionOverflowBlock[];
    noParkingZones: SubmissionNoParkingZone[];
    crosswalks: SubmissionCrosswalk[];
}

const EARTH_RADIUS_METERS = 6378137.0;

function degreesToRadians(value: number): number {
    return (value * Math.PI) / 180;
}

function radiansToDegrees(value: number): number {
    return (value * 180) / Math.PI;
}

function solveLinearSystem(
    matrix: number[][],
    values: number[]
): number[] {
    const n = values.length;

    const augmented = matrix.map((row, index) => [
        ...row,
        values[index],
    ]);

    for (let column = 0; column < n; column++) {
        let pivot = column;

        for (let row = column + 1; row < n; row++) {
            if (
                Math.abs(augmented[row][column]) >
                Math.abs(augmented[pivot][column])
            ) {
                pivot = row;
            }
        }

        if (Math.abs(augmented[pivot][column]) < 1e-12) {
            throw new Error(
                "GPS calibration points produce an invalid transformation."
            );
        }

        [augmented[column], augmented[pivot]] = [
            augmented[pivot],
            augmented[column],
        ];

        const divisor = augmented[column][column];

        for (let j = column; j <= n; j++) {
            augmented[column][j] /= divisor;
        }

        for (let row = 0; row < n; row++) {
            if (row === column) {
                continue;
            }

            const factor = augmented[row][column];

            for (let j = column; j <= n; j++) {
                augmented[row][j] -=
                    factor * augmented[column][j];
            }
        }
    }

    return augmented.map((row) => row[n]);
}

function computeProjectiveTransform(
    source: Point[],
    target: Point[]
): number[][] {
    if (source.length !== 4 || target.length !== 4) {
        throw new Error(
            "Exactly four GPS calibration points are required."
        );
    }

    const matrix: number[][] = [];
    const values: number[] = [];

    for (let i = 0; i < 4; i++) {
        const [x, y] = source[i];
        const [u, v] = target[i];

        matrix.push([
            x, y, 1,
            0, 0, 0,
            -u * x, -u * y,
        ]);
        values.push(u);

        matrix.push([
            0, 0, 0,
            x, y, 1,
            -v * x, -v * y,
        ]);
        values.push(v);
    }

    const h = solveLinearSystem(matrix, values);

    return [
        [h[0], h[1], h[2]],
        [h[3], h[4], h[5]],
        [h[6], h[7], 1],
    ];
}

function gpsToLocalMeters(
    lat: number,
    lon: number,
    originLat: number,
    originLon: number
): Point {
    const north =
        EARTH_RADIUS_METERS *
        degreesToRadians(lat - originLat);

    const east =
        EARTH_RADIUS_METERS *
        degreesToRadians(lon - originLon) *
        Math.cos(degreesToRadians(originLat));

    return [east, north];
}

export function validateSimplePolygon(points: Point[], name: string): void {
    const n = points.length;

    if (n < 3) {
        throw new Error(`${name} requires at least three vertices.`);
    }

    if (points.some(([x, y]) =>
        !Number.isFinite(x) || !Number.isFinite(y)
    )) {
        throw new Error(`${name} contains invalid coordinates.`);
    }

    const orientation = (a: Point, b: Point, c: Point): number =>
        (b[0] - a[0]) * (c[1] - a[1]) -
        (b[1] - a[1]) * (c[0] - a[0]);

    const onSegment = (a: Point, b: Point, p: Point): boolean => {
        const length = Math.hypot(
            b[0] - a[0], b[1] - a[1]
        );
        const tolerance = 1e-10 * Math.max(length, 1e-10);

        return (
            Math.abs(orientation(a, b, p)) <= tolerance &&
            p[0] >= Math.min(a[0], b[0]) - tolerance &&
            p[0] <= Math.max(a[0], b[0]) + tolerance &&
            p[1] >= Math.min(a[1], b[1]) - tolerance &&
            p[1] <= Math.max(a[1], b[1]) + tolerance
        );
    };

    const intersects = (
        a: Point, b: Point, c: Point, d: Point
    ): boolean => {
        const o1 = orientation(a, b, c);
        const o2 = orientation(a, b, d);
        const o3 = orientation(c, d, a);
        const o4 = orientation(c, d, b);

        if ((o1 > 0) !== (o2 > 0) &&
            (o3 > 0) !== (o4 > 0)) {
            return true;
        }

        return (
            (o1 === 0 && onSegment(a, b, c)) ||
            (o2 === 0 && onSegment(a, b, d)) ||
            (o3 === 0 && onSegment(c, d, a)) ||
            (o4 === 0 && onSegment(c, d, b))
        );
    };

    for (let i = 0; i < n; i++) {
        const a = points[i];
        const b = points[(i + 1) % n];

        if (a[0] === b[0] && a[1] === b[1]) {
            throw new Error(`${name} contains a zero-length edge.`);
        }

        for (let j = i + 1; j < n; j++) {
            const adjacent =
                j === i + 1 || (i === 0 && j === n - 1);

            if (adjacent) {
                continue;
            }

            if (intersects(
                a, b, points[j], points[(j + 1) % n]
            )) {
                throw new Error(
                    `${name} contains intersecting edges.`
                );
            }
        }
    }

    // Shoelace area, calculated relative to the first vertex
    // to reduce numerical cancellation for GPS coordinates.
    const origin = points[0];
    let twiceArea = 0;

    for (let i = 1; i < n - 1; i++) {
        twiceArea += orientation(
            origin, points[i], points[i + 1]
        );
    }

    if (Math.abs(twiceArea) <= 1e-14) {
        throw new Error(`${name} has zero or negligible area.`);
    }
}

export function pointInPolygon(point: Point, polygon: Point[]): boolean {
    if (polygon.length < 3) {
        return false;
    }

    // Point coordinates are [latitude, longitude].
    const [y, x] = point;
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [yi, xi] = polygon[i];
        const [yj, xj] = polygon[j];

        // Include points lying directly on a boundary segment.
        const dx = xj - xi;
        const dy = yj - yi;
        const cross = (x - xi) * dy - (y - yi) * dx;
        const lengthSquared = dx * dx + dy * dy;

        if (lengthSquared > 0) {
            const t = ((x - xi) * dx + (y - yi) * dy) / lengthSquared;

            if (
                t >= 0 &&
                t <= 1 &&
                Math.abs(cross) <= 1e-10 * Math.sqrt(lengthSquared)
            ) {
                return true;
            }
        }

        if (
            (yi > y) !== (yj > y) &&
            x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
        ) {
            inside = !inside;
        }
    }

    return inside;
}

export interface SubmissionGPSProjection {
    H: number[][];
    origin: {
        lat: number;
        lon: number;
    };
}

export function buildGPSProjection(
    points: SubmissionGPSPoint[]
): SubmissionGPSProjection {
    if (points.length !== 4) {
        throw new Error(
            "Four GPS calibration points are required."
        );
    }

    const originLat = points[0].lat;
    const originLon = points[0].lon;

    const pixelPoints: Point[] = points.map(
        (point) => [point.x, point.y]
    );

    const localPoints: Point[] = points.map((point) =>
        gpsToLocalMeters(
            point.lat,
            point.lon,
            originLat,
            originLon
        )
    );

    const H = computeProjectiveTransform(
        pixelPoints,
        localPoints
    );

    // A homography must not cross its projective horizon
    // within the calibrated image area.
    const xs = pixelPoints.map(([x]) => x);
    const ys = pixelPoints.map(([, y]) => y);

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const denominators = [
        [minX, minY],
        [maxX, minY],
        [maxX, maxY],
        [minX, maxY],
    ].map(([x, y]) =>
        H[2][0] * x + H[2][1] * y + H[2][2]
    );

    const minimum = Math.min(...denominators);
    const maximum = Math.max(...denominators);

    if (
        denominators.some((value) => !Number.isFinite(value)) ||
        (minimum <= 0 && maximum >= 0)
    ) {
        throw new Error(
            "GPS calibration produces an unstable projection. Reposition the four calibration anchors across the yard."
        );
    }

    return {
        H,
        origin: {
            lat: originLat,
            lon: originLon,
        },
    };
}

export function pixelToGPS(
    point: Point,
    projection: SubmissionGPSProjection
): Point {
    const H = projection.H;
    const [x, y] = point;

    const denominator =
        H[2][0] * x +
        H[2][1] * y +
        H[2][2];

    if (Math.abs(denominator) < 1e-12) {
        throw new Error(
            "Cannot convert this pixel coordinate to GPS."
        );
    }

    const east =
        (
            H[0][0] * x +
            H[0][1] * y +
            H[0][2]
        ) / denominator;

    const north =
        (
            H[1][0] * x +
            H[1][1] * y +
            H[1][2]
        ) / denominator;

    const latitude =
        projection.origin.lat +
        radiansToDegrees(
            north / EARTH_RADIUS_METERS
        );

    const longitude =
        projection.origin.lon +
        radiansToDegrees(
            east /
            (
                EARTH_RADIUS_METERS *
                Math.cos(
                    degreesToRadians(
                        projection.origin.lat
                    )
                )
            )
        );

    return [latitude, longitude];
}

export function getParkingGeometry(
    space: SubmissionParkingSpace,
    metersPerPx: number
): {
    corner1: Point;
    corner2: Point;
    center: Point;
} {
    if (metersPerPx <= 0) {
        throw new Error(
            "A valid yard scale is required."
        );
    }

    const pixelsPerFoot =
        0.3048 / metersPerPx;

    const lengthPx =
        space.lengthFt * pixelsPerFoot;

    const widthPx =
        space.widthFt * pixelsPerFoot;

    const radians =
        degreesToRadians(space.angle);

    const headingX = Math.sin(radians);
    const headingY = -Math.cos(radians);

    const sideX = Math.cos(radians);
    const sideY = Math.sin(radians);

    const [anchorX, anchorY] = space.anchor;

    const backX =
        anchorX - headingX * lengthPx;

    const backY =
        anchorY - headingY * lengthPx;

    const corner1: Point = [
        anchorX + sideX * widthPx / 2,
        anchorY + sideY * widthPx / 2,
    ];

    const corner2: Point = [
        backX - sideX * widthPx / 2,
        backY - sideY * widthPx / 2,
    ];

    const center: Point = [
        (anchorX + backX) / 2,
        (anchorY + backY) / 2,
    ];

    return {
        corner1,
        corner2,
        center,
    };
}

export function buildParkingData(
    spaces: SubmissionParkingSpace[],
    metersPerPx: number,
    projection: SubmissionGPSProjection
): Record<string, unknown> {
    return {
        parking_spaces: spaces.map((space) => {
            const geometry =
                getParkingGeometry(
                    space,
                    metersPerPx
                );

            return {
                parking_space_number:
                    space.id || space.label,

                location: null,

                trailer_number: null,
                trailer_type: null,
                trailer_color: null,
                trailer_length: null,
                trailer_skirts: null,

                trailer_number_2: null,
                trailer_type_2: null,
                trailer_color_2: null,
                trailer_length_2: null,
                trailer_skirts_2: null,

                empty_or_full: null,

                center_px: geometry.center,

                center_ll: pixelToGPS(
                    geometry.center,
                    projection
                ),
                inspection_px: space.anchor,
                inspection_ll: pixelToGPS(space.anchor, projection),
            };
        }),
    };
}

export function buildSetupData(
    input: SetupSubmissionInput
): Record<string, unknown> {
    const {
        yardInfo,
        imageWidth,
        imageHeight,
        gpsPoints,
        geofencePoints,
        scalePxLength,
        scaleFeet,
        metersPerPx,
        parkingSpaces,
        sections,
        buildings,
        pod,
        scanners,
        overflowBlocks,
        noParkingZones,
        crosswalks,
    } = input;

    if (
        metersPerPx === null ||
        !Number.isFinite(metersPerPx) ||
        metersPerPx <= 0
    ) {
        throw new Error(
            "Complete yard scale calibration before submitting."
        );
    }

    if (
        gpsPoints.length !== 4 ||
        gpsPoints.some((point) => point === null)
    ) {
        throw new Error(
            "All four GPS calibration points are required."
        );
    }

    const validGPSPoints =
        gpsPoints.filter(
            (point): point is SubmissionGPSPoint =>
                point !== null
        );

    const projection =
        buildGPSProjection(validGPSPoints);

    // Validate the homography over the entire yard image,
    // not only the four GPS calibration anchors.
    if (
        !Number.isFinite(imageWidth) ||
        !Number.isFinite(imageHeight) ||
        imageWidth <= 0 ||
        imageHeight <= 0
    ) {
        throw new Error("Invalid yard image dimensions.");
    }

    const H = projection.H;

    const imageDenominators = [
        [0, 0],
        [imageWidth, 0],
        [imageWidth, imageHeight],
        [0, imageHeight],
    ].map(([x, y]) =>
        H[2][0] * x + H[2][1] * y + H[2][2]
    );

    const minimumImageDenominator = Math.min(...imageDenominators);
    const maximumImageDenominator = Math.max(...imageDenominators);

    if (
        imageDenominators.some((value) => !Number.isFinite(value)) ||
        (minimumImageDenominator <= 0 && maximumImageDenominator >= 0)
    ) {
        throw new Error(
            "GPS projection is unstable across the yard image. Recalibrate using four widely separated GPS anchors."
        );
    }

    // Compare the GPS projection against the independently
    // measured yard scale at multiple image locations.
    const sampleFractions = [0.1, 0.3, 0.5, 0.7, 0.9];
    const sampleStep = Math.min(imageWidth, imageHeight) * 0.01;

    const gpsDistanceMeters = (a: Point, b: Point): number => {
        const [east, north] = gpsToLocalMeters(
            b[0], b[1], a[0], a[1]
        );
        return Math.hypot(east, north);
    };

    for (const fx of sampleFractions) {
        for (const fy of sampleFractions) {
            const x = imageWidth * fx;
            const y = imageHeight * fy;

            const origin = pixelToGPS([x, y], projection);
            const horizontal = pixelToGPS(
                [x + sampleStep, y], projection
            );
            const vertical = pixelToGPS(
                [x, y + sampleStep], projection
            );

            for (const destination of [horizontal, vertical]) {
                const actualScale =
                    gpsDistanceMeters(origin, destination) / sampleStep;

                const ratio = actualScale / metersPerPx;

                if (
                    !Number.isFinite(ratio) ||
                    ratio < 0.5 ||
                    ratio > 2
                ) {
                    throw new Error(
                        "GPS projection scale differs excessively from " +
                        "the measured yard scale. Verify GPS anchor " +
                        "locations and trailer scale calibration."
                    );
                }
            }
        }
    }

    const toGPS = (point: Point): Point =>
        pixelToGPS(point, projection);

    const polygonToGPS = (
        points: Point[]
    ): Point[] =>
        points.map(toGPS);

    const parkingSpaceData =
        parkingSpaces.map((space) => {
            const geometry =
                getParkingGeometry(
                    space,
                    metersPerPx
                );

            return {
                id: space.id,
                label: space.label,
                corner1: geometry.corner1,
                corner2: geometry.corner2,
                angle: space.angle,
                center_point: geometry.center,
                center_ll: toGPS(
                    geometry.center
                ),
                inspection_px: space.anchor,
                inspection_ll: toGPS(space.anchor),
                status: "empty",
            };
        });

    // Validate required flight locations against the GPS geofence.
    // Scanner coordinates are metadata, not flight destinations.
    if (geofencePoints.length < 3) {
        throw new Error(
            "The yard geofence must contain at least three vertices."
        );
    }

    validateSimplePolygon(geofencePoints, "Pixel geofence");

    const gpsGeofence = polygonToGPS(geofencePoints);

    validateSimplePolygon(gpsGeofence, "GPS geofence");

    if (
        gpsGeofence.some(([lat, lon]) =>
            !Number.isFinite(lat) ||
            !Number.isFinite(lon) ||
            lat < -90 ||
            lat > 90 ||
            lon < -180 ||
            lon > 180
        )
    ) {
        throw new Error(
            "The GPS geofence contains invalid coordinates."
        );
    }

    // Flight destinations must be inside both coordinate-space
    // geofences. Scanners are excluded from flight validation.
    const validateFlightPoint = (
        label: string,
        pixelPoint: Point,
        gpsPoint: Point
    ): void => {
        const insidePixels =
            pointInPolygon(pixelPoint, geofencePoints);
        const insideGPS =
            pointInPolygon(gpsPoint, gpsGeofence);

        if (!insidePixels || !insideGPS) {
            throw new Error(
                `${label} failed geofence validation ` +
                `(pixel: ${insidePixels ? "inside" : "outside"}, ` +
                `GPS: ${insideGPS ? "inside" : "outside"}). ` +
                "Check geofence placement and GPS calibration."
            );
        }
    };

    for (const space of parkingSpaceData) {
        validateFlightPoint(
            `Parking space ${space.label || space.id}`,
            space.inspection_px,
            space.inspection_ll
        );
    }

    if (pod) {
        validateFlightPoint(
            "POD_HOME",
            pod.px,
            toGPS(pod.px)
        );
    }

    for (const section of sections) {
        validateFlightPoint(
            `Section ${section.label} entry`,
            section.entry,
            toGPS(section.entry)
        );
    }

    const scannerData =
        scanners.map((scanner) => ({
            id: scanner.id,
            px: scanner.px,
            ll: toGPS(scanner.px),
            orientation_deg:
                scanner.orientationDeg,
        }));

    const primaryScanner =
        scannerData.length > 0
            ? {
                px: scannerData[0].px,
                ll: scannerData[0].ll,
                orientation_deg:
                    scannerData[0].orientation_deg,
            }
            : null;

    return {
        version: "1.8.2",

        yard: {
            name: yardInfo.name,
            address: yardInfo.address,
            zip_code: yardInfo.zipCode,
            altitude_a: Number(
                yardInfo.altitudeA
            ),
            altitude_b: Number(
                yardInfo.altitudeB
            ),
            altitude_c: Number(
                yardInfo.altitudeC
            ),
        },

        image: {
            width: imageWidth,
            height: imageHeight,
            zoom: 1,
            path: null,
        },

        georef: projection,

        gps_points: gpsPoints.map(
            (point) =>
                point
                    ? {
                        x: point.x,
                        y: point.y,
                        lat: point.lat,
                        lon: point.lon,
                        altitude: null,
                    }
                    : null
        ),

        waypoints: validGPSPoints.map(
            (point, index) => ({
                label: `P${index + 1}`,
                px: {
                    x: point.x,
                    y: point.y,
                },
                latitude: point.lat,
                longitude: point.lon,
                altitude: null,
            })
        ),

        geofence: {
            px: geofencePoints,
            ll: polygonToGPS(
                geofencePoints
            ),
        },

        scale:
            scalePxLength !== null
                ? {
                    px_length:
                        scalePxLength,
                    feet: Number(
                        scaleFeet
                    ),
                    meters_per_px:
                        metersPerPx,
                }
                : null,

        measurements: [],

        pod: pod
            ? {
                home_px: pod.px,
                home_ll: toGPS(
                    pod.px
                ),
            }
            : null,

        scanner: primaryScanner,

        scanners: scannerData,

        sections: sections.map(
            (section) => {
                const { x, y, width, height } =
                    section.rect;

                const corners: Point[] = [
                    [x, y],
                    [x + width, y],
                    [x + width, y + height],
                    [x, y + height],
                ];

                return {
                    id: section.id,
                    label: section.label,

                    rect_px: [
                        x,
                        y,
                        width,
                        height,
                    ],

                    entry_px:
                        section.entry,

                    orientation_deg:
                        section.orientationDeg,

                    rect_ll:
                        polygonToGPS(corners),

                    entry_ll:
                        toGPS(section.entry),
                };
            }
        ),

        blocks: overflowBlocks.map(
            (block) => ({
                id: block.id,
                label: block.label,

                polygon_px:
                    block.points,

                polygon_ll:
                    polygonToGPS(
                        block.points
                    ),

                orientation_deg:
                    block.orientationDeg,

                active:
                    block.active,
            })
        ),

        parking: [],

        parking_spaces:
            parkingSpaceData,

        buildings: buildings.map(
            (building) => ({
                id: building.id,
                label: building.label,

                polygon_px:
                    building.points,

                polygon_ll:
                    polygonToGPS(
                        building.points
                    ),
            })
        ),

        no_parking_zones:
            noParkingZones.map(
                (zone) => ({
                    id: zone.id,

                    polygon_px:
                        zone.points,

                    polygon_ll:
                        polygonToGPS(
                            zone.points
                        ),
                })
            ),

        crosswalks: crosswalks.map(
            (crosswalk) => ({
                id: crosswalk.id,

                p1: crosswalk.p1,
                p2: crosswalk.p2,

                width_ft:
                    crosswalk.widthFt,

                p1_ll:
                    toGPS(
                        crosswalk.p1
                    ),

                p2_ll:
                    toGPS(
                        crosswalk.p2
                    ),
            })
        ),
    };
}
